import { zodResolver } from "@hookform/resolvers/zod";
import { useLogout } from "common/hooks/useLogout/useLogout";
import { getList as getCountries } from "country-list-with-dial-code-and-flag";
import { AccountPhoneNumberStatusesEnum, ServerErrors } from "dash-common";
import { Icon, jwtDecodeSafely, localStorage, ViewerContext } from "dash-ui";
import CompleteSignUpPageLayout from "flow-components/CompleteSignUpPageLayout/CompleteSignUpPageLayout";
import { RoutesPath } from "flow-components/routes/routes";
import {
  GetAccountDocument,
  GetCurrentBrokerDocument,
  useCreateBrokerSsoMutation,
  useGetUsStatesQuery,
} from "gql/generated.graphql";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { ImSpinner8 } from "react-icons/im";
import { Navigate, useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import { z } from "zod";

const ssoFormSchema = z
  .object({
    country: z.string().min(1, "Country is required"),
    phoneNumber: z
      .string()
      .min(1, "Phone number is required.")
      .length(10, "Phone number must have 10 numbers.")
      .regex(
        new RegExp(/^[0-9]+$/),
        "Phone number must contains only numbers."
      ),
    licenseNumber: z
      .string()
      .max(20, "License number must have at most 20 characters.")
      .optional(),
    stateCode: z.string().min(1, "State is required"),
    agreement: z.boolean().refine((value) => value === true, {
      message: "You must accept the terms and conditions.",
    }),
  })
  .refine(
    (data) => {
      if (data.country === "US" && !data.stateCode) {
        return false;
      }
      return true;
    },
    {
      message: "State is required.",
      path: ["state"],
    }
  )
  .refine(
    (data) => {
      if (data.country === "US" && !data.licenseNumber) {
        return false;
      }
      return true;
    },
    {
      message: "License number is required.",
      path: ["licenseNumber"],
    }
  );

type FormSchema = z.infer<typeof ssoFormSchema>;

function FormComponent() {
  const theme = useTheme();
  const [logout] = useLogout();
  const {
    account,
    tokens: { accessToken },
  } = useContext(ViewerContext);

  const { email, given_name } = jwtDecodeSafely(accessToken) ?? {};

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<FormSchema>({
    defaultValues: {
      country: "US",
    },
    resolver: zodResolver(ssoFormSchema),
  });

  const countries = getCountries();

  const selectedCountry = countries.find(
    (country) => country.code === watch("country")
  );

  const getUsStates = useGetUsStatesQuery();
  const shouldRenderUSInfo = watch("country") === "US";

  const [createBrokerSSO, { loading }] = useCreateBrokerSsoMutation({
    refetchQueries: [GetAccountDocument, GetCurrentBrokerDocument],
  });

  const onSubmit = async (data: FormSchema) => {
    const { phoneNumber, licenseNumber, stateCode } = data;

    const dialCode =
      countries.find((country) => country.code === data.country)?.dial_code ||
      "";

    await createBrokerSSO({
      variables: {
        input: {
          phoneNumber,
          licenseNumber,
          dialCode,
          stateCode,
        },
      },
      onError: (error) => {
        if (error.message === ServerErrors.LICENSE_ALREADY_EXISTS) {
          setError("licenseNumber", {
            message: "License number already exists.",
          });
        }
        if (error.message === ServerErrors.PHONE_NUMBER_IS_USED) {
          setError("phoneNumber", {
            message: "Phone number already exists.",
          });
        }
      },
      onCompleted: (data) => {
        const { accessToken, refreshToken, refreshTokenExpiry } =
          data.createBrokerSSO || {};

        localStorage.setAccessToken(accessToken);
        localStorage.setRefreshToken(refreshToken);
        localStorage.setRefreshTokenExpiry(refreshTokenExpiry || "");

        window.dispatchEvent(new Event("storage"));
      },
    });
  };

  if (account?.phone?.status === AccountPhoneNumberStatusesEnum.VERIFIED) {
    return <Navigate to={RoutesPath.CompleteSignUpAddAdditionalInfo} />;
  }

  return (
    <form
      id="sso-form"
      className="sso-form flex flex-col gap-4 w-full px-12 mt-12 sm:px-24 md:px-32"
      onSubmit={handleSubmit(onSubmit)}>
      <h2 className="text-2xl font-bold">Welcome, {given_name}!</h2>
      <h2 className="text-md text-slate-700">
        <Icon
          className="inline-block mr-2"
          name="checkCircle"
          color={theme.colors.g20}
        />
        You have been successfully authenticated with Miami Realtors. Please
        complete your profile before proceeding. <br />
        <div className="mt-2">
          <Icon
            className="inline-block mr-1"
            name="email"
            color={theme.colors.b50}
          />{" "}
          {email}
          <p className="text-xs mt-1">
            Want to use a different e-mail?{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                logout();
              }}>
              Use normal sign up
            </a>{" "}
            instead.
          </p>
        </div>
      </h2>

      <div className="flex flex-col">
        <label className="text-xs text-slate-500">Country</label>
        <select
          className="flex items-center h-12 px-3 rounded-lg border border-slate-400 invalid:text-slate-400"
          {...register("country")}>
          <option disabled hidden>
            Select your country
          </option>

          {countries.map((country) => {
            return (
              <option key={country.name} value={country?.code}>
                {country?.name}
              </option>
            );
          })}
        </select>
        <span className="text-sm text-red-600">{errors.country?.message}</span>
      </div>

      <div className="flex flex-col">
        <label className="text-xs text-slate-500">Phone Number</label>
        <div
          className={`flex h-12 pl-3 items-center gap-3 w-full rounded-lg border ${
            errors.phoneNumber ? "border-red-600" : "border-slate-400"
          } has-[:focus]:outline has-[:focus]:outline-1 bg-white`}>
          <p className="min-w-fit">{selectedCountry?.dial_code}</p>
          <input
            {...register("phoneNumber", { maxLength: 10 })}
            placeholder="1112223333"
            type="tel"
            maxLength={10}
            className="w-full rounded bg-transparent outline-none"
          />
        </div>
        <span className="text-red-600 text-sm">
          {errors.phoneNumber?.message}
        </span>
      </div>

      {shouldRenderUSInfo && (
        <div className="flex gap-4 flex-col md:flex-row">
          <div className="flex flex-col flex-1">
            <label className="text-xs text-slate-500">State</label>
            <select
              className={`h-12 flex items-center px-3 rounded-lg border invalid:text-slate-400 ${
                errors.stateCode ? "border-red-600" : "border-slate-400"
              }`}
              defaultValue={""}
              {...register("stateCode")}>
              <option value="" disabled hidden>
                Select your state
              </option>

              {getUsStates.data?.getUSStates.map((state) => {
                return (
                  <option key={state._id} value={state?.code}>
                    {state?.name}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-red-600">
              {errors.stateCode?.message}
            </span>
          </div>

          <div className="flex flex-col flex-1">
            <label className="text-xs text-slate-500">
              Agent License Number
            </label>
            <input
              placeholder="123456789"
              type="text"
              className={`h-12 flex items-center px-3 rounded-lg border ${
                errors.licenseNumber ? "border-red-600" : "border-slate-400"
              }`}
              {...register("licenseNumber", { maxLength: 10 })}
              maxLength={10}
            />
            <span className="text-red-600 text-sm">
              {errors.licenseNumber?.message}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col items-start mt-4">
        <div className="flex flex-row gap-2 items-center">
          <input
            {...register("agreement")}
            type="checkbox"
            name="agreement"
            className="flex h-5 w-5"
          />
          <label className="flex-1" htmlFor="agreement">
            I agree to the{" "}
            <a href="http://localhost:3050/term-conditions" target="_blank">
              terms and conditions
            </a>{" "}
            and{" "}
            <a href="http://localhost:3050/privacy-policy" target="_blank">
              privacy policy
            </a>
            .
          </label>
        </div>
        <span className="text-sm text-red-600">
          {errors.agreement?.message}
        </span>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="flex h-12 mb-8 items-center justify-center bg-sky-600 px-3 rounded text-white mt-4 disabled:opacity-30">
        {loading ? <ImSpinner8 className="animate-spin" /> : "Finish sign up"}
      </button>
    </form>
  );
}

function CompleteSignUpSSOPage() {
  return (
    <CompleteSignUpPageLayout
      activeStep={0}
      withProfileDropdown={false}
      withLogoutButton>
      <FormComponent />
    </CompleteSignUpPageLayout>
  );
}

export default CompleteSignUpSSOPage;
